import { ContractGetQuery, EProposalFileType, useCustomerGetQuery } from '~/graphql/types'
import { StepTitle } from '../StepTitle'
import { FieldsType } from '../types'
import { useForm, FormProvider, useWatch, Controller } from 'react-hook-form'
import { Box, Button, Typography, Checkbox as MuiCheckbox, FormControlLabel, useMediaQuery } from '@mui/material'
import { useEffect } from 'react'
import { Input } from '~/components/Form/Input'
import { InputPendenciesStatus } from '../InputPendenciesStatus'
import { Checkbox } from '~/components/Form/Checkbox'
import { IconCheck, IconX } from '@tabler/icons-react'
import { theme } from '~/utils'
import { FilePendencyStatus } from '../FilePendencyStatus'
import { useAuth } from '~/contexts'

type RepresentativesProps = {
  contract: ContractGetQuery['contractGet']
  onRefuse: Function
  onApprove: Function
  modalType: 'approve' | 'refuse'
  setFields: React.Dispatch<React.SetStateAction<FieldsType>>
  representativeIndex: number
  setRepresentativeIndex: React.Dispatch<React.SetStateAction<number>>
  onRefetchCustomer?: Function
}

type RepresentativeForm = {
  name: string
  document: string
  email: string
  phone: string
  zipcode: string
  state: string
  city: string
  neighborhood: string
  number: string
  complement: string
  pendencies: {
    name: boolean
    document: boolean
    email: boolean
    phone: boolean
    zipCode: boolean
    state: boolean
    city: boolean
    neighborhood: boolean
    number: boolean
    complement: boolean
    customerPjRepresentativeDocumentWithPhotoFront: boolean
    customerPjRepresentativeDocumentWithPhotoBack: boolean
  }
  controlPendencies: boolean
}

export const Representatives: React.FC<RepresentativesProps> = ({ onApprove, onRefuse, contract, setFields, representativeIndex, setRepresentativeIndex }) => {
  const formMethods = useForm<RepresentativeForm>()
  const { authUser } = useAuth()
  const isLowerSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isSeller = authUser?.user.type === 'seller'

  const { data: customerData } = useCustomerGetQuery({
    variables: {
      id: contract?.customerId || ''
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  })

  // Pegar o representante atual baseado no índice
  const currentRepresentative = customerData?.customerGet?.legalRepresentatives?.[representativeIndex]
  const currentRepresentativeContract = contract?.customerRef?.legalRepresentatives?.[representativeIndex]
  console.log(currentRepresentativeContract)

  // Preencher os campos do formulário com os dados do representante atual
  useEffect(() => {
    if (currentRepresentative && contract) {
      formMethods.setValue('name', currentRepresentative.name || '')
      formMethods.setValue('document', currentRepresentative.document || '')
      formMethods.setValue('email', currentRepresentative.email || '')
      formMethods.setValue('phone', currentRepresentative.phone || '')
      formMethods.setValue('zipcode', currentRepresentative.address?.zipcode || '')
      formMethods.setValue('state', currentRepresentative.address?.state || '')
      formMethods.setValue('city', currentRepresentative.address?.city || '')
      formMethods.setValue('neighborhood', currentRepresentative.address?.neighborhood || '')
      formMethods.setValue('number', currentRepresentative.address?.number || '')
      formMethods.setValue('complement', currentRepresentative.address?.complement || '')

      // Inicializar pendências como false
      formMethods.setValue('pendencies', {
        name: false,
        document: false,
        email: false,
        phone: false,
        zipCode: false,
        state: false,
        city: false,
        neighborhood: false,
        number: false,
        complement: false,
        customerPjRepresentativeDocumentWithPhotoFront: false,
        customerPjRepresentativeDocumentWithPhotoBack: false,
      })
    }
  }, [currentRepresentative, contract, formMethods])

  function handleSubmit(data: RepresentativeForm) {
    const selectedFields = Object?.entries(data?.pendencies).filter(item => item[1])
    const objectFields = Object.fromEntries(selectedFields)
    setFields(objectFields)
  }

  const { pendencies } = useWatch({
    control: formMethods.control,
  })

  function onSelectAllFields(isValid: boolean) {
    formMethods.setValue('controlPendencies', !isValid)
    formMethods.setValue('pendencies.name', !isValid)
    formMethods.setValue('pendencies.document', !isValid)
    formMethods.setValue('pendencies.email', !isValid)
    formMethods.setValue('pendencies.phone', !isValid)
    formMethods.setValue('pendencies.zipCode', !isValid)
    formMethods.setValue('pendencies.state', !isValid)
    formMethods.setValue('pendencies.city', !isValid)
    formMethods.setValue('pendencies.neighborhood', !isValid)
    formMethods.setValue('pendencies.number', !isValid)
    if (currentRepresentative?.address?.complement) {
      formMethods.setValue('pendencies.complement', !isValid)
    }
    formMethods.setValue('pendencies.customerPjRepresentativeDocumentWithPhotoFront', !isValid)
    formMethods.setValue('pendencies.customerPjRepresentativeDocumentWithPhotoBack', !isValid)
  }

  // Verificar se há pendências rejeitadas para habilitar a seleção
  const enableSelectAll = [
    Boolean(!currentRepresentative?.name),
    Boolean(!currentRepresentative?.document),
    Boolean(!currentRepresentative?.email),
    Boolean(!currentRepresentative?.phone),
    Boolean(!currentRepresentative?.address?.zipcode),
    Boolean(!currentRepresentative?.address?.state),
    Boolean(!currentRepresentative?.address?.city),
    Boolean(!currentRepresentative?.address?.neighborhood),
    Boolean(!currentRepresentative?.address?.number),
    // Incluir verificação dos arquivos dos representantes legais
    Boolean(!currentRepresentativeContract?.files?.find(file => file.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront)?.file?.fileUrl),
    Boolean(!currentRepresentativeContract?.files?.find(file => file.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack)?.file?.fileUrl)
  ].some(item => item)

  // Verificar se há documentos rejeitados
  // const enableSelectAllWithDocuments = Boolean(currentRepresentativeContract?.files?.some(item =>
  //   (item.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoFront ||
  //     item.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack) &&
  //   item.status === 'rejected'
  // ))

  const buttonIsEnable = pendencies && Object?.values(pendencies)?.some(item => item)

  return (
    <>
      <StepTitle contractId={contract?._id} clientName={contract?.customerRef.name} step={{ value: '4', title: 'Representantes' }} />

      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit((data) => handleSubmit(data))}>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem 0' }}>
            <Typography sx={{ fontSize: isLowerSm ? '.95rem' : '1.1rem' }} variant='h6'>
              {currentRepresentative?.name || `Representante ${representativeIndex + 1}`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              {!isLowerSm &&
                <Typography fontWeight={500} color={theme.palette.grey[600]} fontSize='.95rem' variant='h6'>Selecionar todos</Typography>
              }
              <Controller
                name='controlPendencies'
                control={formMethods.control}
                defaultValue={false}
                render={({ field }) => (
                  <FormControlLabel
                    {...field}
                    sx={{ margin: 0 }}
                    onChange={(e: any) => {
                      field.onChange(e)
                      onSelectAllFields(field.value)
                    }}
                    control={<MuiCheckbox disabled={(enableSelectAll)} />}
                    label={<Typography variant='body2' color='grey.400'></Typography>}
                  />
                )}
              />
            </Box>
          </Box>

          <Typography sx={{ padding: '1rem 0 0 0' }} variant='h6'>Dados pessoais</Typography>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.name} /> } }}
              disabled
              name='name'
              label='Nome'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.name) }} name='pendencies.name' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.document} /> } }}
              disabled
              mask='cpf'
              name='document'
              label='CPF'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.document) }} name='pendencies.document' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.email} /> } }}
              disabled
              name='email'
              label='E-mail'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.email) }} name='pendencies.email' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.phone} /> } }}
              disabled
              mask='phone'
              name='phone'
              label='Telefone'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.phone) }} name='pendencies.phone' />
          </Box>

          <Typography sx={{ padding: '1rem 0 0 0' }} variant='h6'>Endereço</Typography>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              mask='cep'
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.zipCode} /> } }}
              disabled
              name='zipcode'
              label='CEP'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.address?.zipcode) }} name='pendencies.zipCode' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.state} /> } }}
              disabled
              name='state'
              label='Estado'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.address?.state) }} name='pendencies.state' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.city} /> } }}
              disabled
              name='city'
              label='Cidade'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.address?.city) }} name='pendencies.city' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.neighborhood} /> } }}
              disabled
              name='neighborhood'
              label='Bairro'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.address?.neighborhood) }} name='pendencies.neighborhood' />
          </Box>

          <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
            <Input
              icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.number} /> } }}
              disabled
              name='number'
              label='Número'
            />
            <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.address?.number) }} name='pendencies.number' />
          </Box>

          {currentRepresentative?.address?.complement && (
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '0 0 1rem 0' }}>
              <Input
                icons={{ end: { element: <InputPendenciesStatus pendencie={currentRepresentativeContract?.pendencies?.complement} /> } }}
                disabled
                name='complement'
                label='Complemento'
              />
              <Checkbox checkboxProps={{ disabled: Boolean(!currentRepresentative?.address?.complement) }} name='pendencies.complement' />
            </Box>
          )}

          <Typography sx={{ padding: '1rem 0 0 0' }} variant='h6'>Documentos</Typography>

          {currentRepresentativeContract?.files?.map((file) => {
            return (
              <Box key={file.type} sx={{ display: 'flex', gap: '1rem', alignItems: 'center', padding: '1rem 0 1rem 0' }}>
                <FilePendencyStatus
                  label={file.type === EProposalFileType.customerPjRepresentativeDocumentWithPhotoBack ? 'Anexar RG ou CNH (Verso)' : 'Anexar RG ou CNH (Frente)'}
                  fileName={file?.file?.fileName}
                  fileUrl={file?.file?.key ? `${process.env.VITE_APP_REST_API_URL}files/download/file/${file?.file?.key}` : file?.file?.fileUrl}
                  status={file?.status}
                />
                <Checkbox checkboxProps={{ disabled: Boolean(!file?.file?.fileUrl) }} name={`pendencies.${file.type}`} />
              </Box>
            )
          })}

          {/* Navegação entre representantes */}
          {customerData?.customerGet?.legalRepresentatives && customerData.customerGet.legalRepresentatives.length > 1 && (
            <Box sx={{ display: 'flex', gap: '1rem', justifyContent: 'center', padding: '2rem 0 1rem 0' }}>
              <Button
                variant='outlined'
                disabled={representativeIndex === 0}
                onClick={() => setRepresentativeIndex(representativeIndex - 1)}
              >
                Anterior
              </Button>
              <Typography sx={{ display: 'flex', alignItems: 'center', padding: '0 1rem' }}>
                {representativeIndex + 1} de {customerData.customerGet.legalRepresentatives.length}
              </Typography>
              <Button
                variant='outlined'
                disabled={representativeIndex === customerData.customerGet.legalRepresentatives.length - 1}
                onClick={() => setRepresentativeIndex(representativeIndex + 1)}
              >
                Próximo
              </Button>
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
              padding: '2rem 0',
              flexFlow: isLowerSm ? 'column' : 'row'
            }}
          >
            <Button
              disabled={!buttonIsEnable || isSeller}
              onClick={() => onRefuse()}
              type='submit'
              startIcon={<IconX />}
              color='secondary'>
              Recusar selecionados
            </Button>
            <Button
              disabled={!buttonIsEnable || isSeller}
              onClick={() => onApprove()}
              type='submit'
              startIcon={<IconCheck />}>
              Aprovar selecionados
            </Button>
          </Box>

        </form>
      </FormProvider>
    </>
  )
}
